import { SignedIn, SignedOut, SignUpButton } from '@clerk/clerk-react'
import { Trans as T } from '@lingui/react/macro'
import posthog from 'posthog-js'
import { match } from 'ts-pattern'

import { cva, cx, VariantProps } from '@/lib/cva'
import { Footer } from '~/components/app/Footer'
import { Header } from '~/components/app/Header'
import { Center } from '~/components/layouts/Center'
import { Arrow } from '~/components/svg/Arrow'
import { Button } from '~/components/ui/Button'
import { Icon } from '~/components/ui/Icon'

export function Landing() {
  const ctaLabel = match(posthog.getFeatureFlag('cta-label-variations'))
    .with('try-it-now-for-free', () => <T>Get Started — It&apos;s Free</T>)
    .with('get-started', () => <T>Get Started</T>)
    .otherwise(() => <T>Try it now</T>) // control

  return (
    <>
      <div className="flex min-h-screen flex-col bg-slate-900">
        <Header />
        <div className="flex flex-1 flex-col gap-4 px-2 md:px-4">
          {/* Hero Section */}
          <Center className="flex flex-1 flex-col justify-end gap-16">
            <div className="flex flex-col gap-4">
              <h1 className="semicasual hero-gradient max-w-prose py-1 text-6xl leading-none font-black [text-wrap:pretty]">
                <T>Never lose track of your crowdfunding pledges again.</T>
              </h1>
              <p className="max-w-prose text-lg leading-tight [text-wrap:pretty] text-violet-300">
                <T>
                  Track your campaigns from <strong>Kickstarter</strong>, <strong>Gamefound</strong>, and <em>more</em> in one place. Get
                  reminded of important updates, pledge manager deadlines, and delivery status changes.
                </T>
              </p>
            </div>
            {/* TODO: This better. */}
            <div className="grid w-fit grid-cols-1 gap-2 gap-x-4 place-self-center justify-self-center md:grid-cols-2 md:place-self-start md:justify-self-start">
              <SignedIn>
                <Button data-attr="cta-authed" icon="rocket-launch" intent="primary" to="/projects" variant="solid">
                  {ctaLabel}
                </Button>
              </SignedIn>
              <SignedOut>
                <SignUpButton mode="modal">
                  <Button data-attr="cta" icon="rocket-launch" intent="primary" variant="solid">
                    {ctaLabel}
                  </Button>
                </SignUpButton>
              </SignedOut>
              <div className="relative inline-block">
                <Button data-attr="learn-more" href="/#learn-more" intent="secondary">
                  <T>Learn more</T>
                </Button>
                <Arrow className="absolute inset-x-full inset-y-1/2 z-10 ml-4 w-16 text-fuchsia-500" />
              </div>
              <p className="casual row-start-2 flex items-center gap-2 text-sm font-semibold text-amber-200 italic">
                <Icon className="text-green-300" name="circle-check" swapOpacity />
                <T>For real; no credit card required.</T>
              </p>
            </div>
            <figure className="relative w-full shadow-2xl">
              <div className="absolute inset-x-0 bottom-0 z-10 h-1/4 bg-gradient-to-b from-transparent to-slate-900" />
              <img
                alt="Screenshot of BackerQuest"
                className="w-full rounded-t-md border border-slate-400"
                height={476}
                src="/images/project.png"
                width={1024}
              />
            </figure>
          </Center>
        </div>
      </div>

      {/* Features Section */}
      <div className="z-10 bg-slate-800 px-4 py-16" id="learn-more">
        <Center className="flex flex-col gap-16">
          <h2 className="text-center text-3xl font-bold text-teal-400 md:text-5xl">
            <T>Your Crowdfunding Companion</T>
          </h2>
          <dl className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {features.map(({ description, icon, name }, i) => (
              <div className="relative pl-16" key={i}>
                <dt className="semibold text-base/7 font-bold">
                  <figure className={cx('absolute top-0 left-0 flex size-10 items-center justify-center rounded-lg', iconStyle({ icon }))}>
                    <Icon name={icon} size="lg" />
                  </figure>
                  {name}
                </dt>
                <dd className="mt-2 text-base/7">{description}</dd>
              </div>
            ))}
          </dl>
        </Center>
      </div>

      <div className="z-10 bg-slate-900 px-4">
        <Center className="shadow-pane z-20 -my-8 rounded-lg bg-slate-700 px-4 py-16">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {benefits.map(({ description, icon, name }, i) => (
              <div className="flex flex-col items-center gap-4 text-center" key={i}>
                <div className={cx('mx-auto flex h-16 w-16 items-center justify-center rounded-full', iconStyle({ icon }))}>
                  <Icon name={icon} size="2x" />
                </div>
                <h3 className="text-xl font-bold">{name}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </Center>
      </div>

      <div className="flex min-h-96 flex-col items-center justify-around bg-gradient-to-br from-fuchsia-700/10 to-violet-900/50 p-4 text-center">
        <h2 className="text-4xl font-bold text-violet-300">
          <T>Ready to begin your adventure?</T>
        </h2>
        <div className="">
          <Button intent="secondary" size="lg" variant="solid">
            <T>Get Started — It&apos;s Free</T>
          </Button>
        </div>
        <p className="flex flex-col items-end gap-1 text-sm text-fuchsia-400">
          <q className="casual font-semibold italic">
            <T>…because spreadsheets are for taxes, not treasures.</T>
          </q>
          <span className="semicasual oblique -mr-12 rotate-2 text-fuchsia-300">{'— Abraham Lincoln'}</span>
        </p>
      </div>
      <Footer />
    </>
  )
}

const iconStyle = cva({
  variants: {
    icon: {
      compass: 'bg-indigo-300 text-indigo-800',
      'crystal-ball': 'bg-sky-200 text-sky-800',
      heart: 'bg-fuchsia-300 text-fuchsia-800',
      newspaper: 'bg-neutral-300 text-neutral-800',
      radar: 'bg-green-300 text-green-800',
      sparkles: 'bg-violet-300 text-violet-800',
      stopwatch: 'bg-amber-100 text-amber-800',
    },
  },
})

const features: { description: React.ReactNode; icon: NonNullable<VariantProps<typeof iconStyle>['icon']>; name: React.ReactNode }[] = [
  {
    description: (
      <T>
        Watch your collection come to life automatically as BackerQuest works its magic. No more spreadsheets, no more forgotten pledges —
        just pure organization bliss.
      </T>
    ),
    icon: 'sparkles',
    name: <T>Magic Import</T>,
  },
  {
    description: (
      <T>
        Never miss a beat in your backer journey. Get gentle nudges for the moments that matter, from pledge managers to shipping updates.
      </T>
    ),
    icon: 'stopwatch',
    name: <T>Perfect Timing</T>,
  },
  {
    description: <T>Cut through the noise with smart summaries that highlight what matters. Stay informed without getting overwhelmed.</T>,
    icon: 'newspaper',
    name: <T>Summarized Updates</T>,
  },
  {
    description: (
      <T>
        Watch your backed projects evolve from ambitious ideas to delivered reality. Every milestone, every update, all in one beautiful
        dashboard.
      </T>
    ),
    icon: 'compass',
    name: <T>Journey Tracker</T>,
  },
]

const benefits: { description: React.ReactNode; icon: NonNullable<VariantProps<typeof iconStyle>['icon']>; name: React.ReactNode }[] = [
  {
    description: <T>Focus on discovering amazing projects, knowing BackerQuest has your back for all the important details.</T>,
    icon: 'heart',
    name: <T>Back with Confidence</T>,
  },
  {
    description: (
      <T>
        Experience the{' '}
        <del className="relative font-bold text-slate-100/50 no-underline after:absolute after:top-1/2 after:left-1/2 after:block after:-translate-1/2 after:scale-120 after:-rotate-12 after:text-transparent after:line-through after:decoration-fuchsia-300 after:content-['FOMO'/'']">
          <abbr title="Fear of Missing Out">FOMO</abbr>
        </del>{' '}
        <ins className="casual font-bold text-fuchsia-300 italic">joy</ins> of backing projects without the stress of tracking deadlines and
        updates manually.
      </T>
    ),
    icon: 'crystal-ball',
    name: <T>Stay in the Loop</T>,
  },
  {
    description: <T>From pledge managers to shipping updates, get timely reminders for the moments that matter most.</T>,
    icon: 'radar',
    name: <T>Never Miss Out</T>,
  },
]
